import { ShipInfo } from '@ais-ui/websockets';
import { useGetShipByMmsi } from '@ais-ui/api';
import { LoadingSpinner, Separator } from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { isNonEmpty } from '@ais-ui/utils';
import { VesselResult } from '@ais-ui/api-spec/shipdb';
import { extractMmsiFromShipInfo } from '../../utils/extractMmsiFromShipInfo';
import { ErrorDisplay } from '../../../components/ErrorDisplay';
import React from 'react';
import { useAuth } from '@ais-ui/auth';
import { usePermissions } from '../../Permissions/PermissionsProvider';

interface BeneficialOwnerItemProps {
  label: string;
  value: string;
}

const BeneficialOwnerItem = ({ label, value }: BeneficialOwnerItemProps) => {
  return (
    <div className={'flex flex-col mt-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs select-text">
        {isNonEmpty(value) ? value : '-'}
      </span>
    </div>
  );
};

const BeneficialOwner = ({ advancedRegister }: { advancedRegister: any }) => {
  const { messages } = useLocalization();
  const { formatDateWithTimeZone } = useDateTimeFormat();

  return (
    <div className="grid grid-cols-2 gap-1">
      <BeneficialOwnerItem
        label={messages.shipDetailsRegisterBeneficialOwnerNameLabel}
        value={advancedRegister.beneficialOwner}
      ></BeneficialOwnerItem>
      <BeneficialOwnerItem
        label={messages.shipDetailsRegisterBeneficialOwnerCityLabel}
        value={advancedRegister.beneficialOwnerCity}
      ></BeneficialOwnerItem>
      <BeneficialOwnerItem
        label={messages.shipDetailsRegisterBeneficialOwnerCountryOfControlLabel}
        value={advancedRegister.beneficialOwnerCountryOfControl}
      ></BeneficialOwnerItem>
      <BeneficialOwnerItem
        label={
          messages.shipDetailsRegisterBeneficialOwnerCountryOfDomicileLabel
        }
        value={advancedRegister.beneficialOwnerCountryOfDomicile}
      ></BeneficialOwnerItem>
      <BeneficialOwnerItem
        label={
          messages.shipDetailsRegisterBeneficialOwnerCountryOfRegistrationLabel
        }
        value={advancedRegister.beneficialOwnerCountryOfRegistration}
      ></BeneficialOwnerItem>
      <BeneficialOwnerItem
        label={messages.shipDetailsRegisterBeneficialOwnerEffectiveDateLabel}
        value={formatDateWithTimeZone(
          advancedRegister.beneficialOwnerEffectiveDate
        )}
      ></BeneficialOwnerItem>
    </div>
  );
};

interface ClassificationSocietyItemProps {
  label: string;
  value: string;
}

const ClassificationSocietyItem = ({
  label,
  value,
}: ClassificationSocietyItemProps) => {
  return (
    <div className={'flex flex-col mt-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs select-text">
        {isNonEmpty(value) ? value : '-'}
      </span>
    </div>
  );
};

const ClassificationSociety = ({
  advancedRegister,
}: {
  advancedRegister: any;
}) => {
  const { messages } = useLocalization();
  const { formatDateWithTimeZone } = useDateTimeFormat();

  return (
    <div className="grid grid-cols-2 gap-1">
      <ClassificationSocietyItem
        label={messages.shipDetailsRegisterClassificationSocietyNameLabel}
        value={advancedRegister?.classificationSociety}
      ></ClassificationSocietyItem>
      <ClassificationSocietyItem
        label={
          messages.shipDetailsRegisterClassificationSocietyEffectiveDateLabel
        }
        value={formatDateWithTimeZone(
          advancedRegister?.classificationSocietyEffectiveDate
        )}
      ></ClassificationSocietyItem>
    </div>
  );
};

interface ISMManagerItemProps {
  label: string;
  value: string;
}

const ISMManagerItem = ({ label, value }: ISMManagerItemProps) => {
  return (
    <div className={'flex flex-col mt-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs select-text">
        {isNonEmpty(value) ? value : '-'}
      </span>
    </div>
  );
};

const ISMManager = ({ advancedRegister }: { advancedRegister: any }) => {
  const { messages } = useLocalization();
  const { formatDateWithTimeZone } = useDateTimeFormat();

  return (
    <div className="grid grid-cols-2 gap-1">
      <ISMManagerItem
        label={messages.shipDetailsRegisterISMManagerNameLabel}
        value={advancedRegister?.ismManager}
      ></ISMManagerItem>
      <ISMManagerItem
        label={messages.shipDetailsRegisterISMManagerCityLabel}
        value={advancedRegister?.ismManagerCity}
      ></ISMManagerItem>
      {/*<ISMManagerItem
        label={messages.shipDetailsRegisterISMManagerCountryOfControlLabel}
        value={advancedRegister.ismManagerCountryOfControl}
      ></ISMManagerItem> */}
      <ISMManagerItem
        label={messages.shipDetailsRegisterISMManagerCountryOfDomicileLabel}
        value={advancedRegister?.ismManagerCountryOfDomicile}
      ></ISMManagerItem>
      {/*<ISMManagerItem
        label={messages.shipDetailsRegisterISMManagerCountryOfRegistrationLabel}
        value={advancedRegister.ismManagerCountryOfRegistration}
      ></ISMManagerItem> */}
      <ISMManagerItem
        label={messages.shipDetailsRegisterISMManagerEffectiveDateLabel}
        value={formatDateWithTimeZone(
          advancedRegister?.ismManagerEffectiveDate
        )}
      ></ISMManagerItem>
    </div>
  );
};

interface RegisterItemProps {
  label: string;
  value: string;
}

const RegisterItem = ({ label, value }: RegisterItemProps) => {
  return (
    <div className={'flex flex-col mt-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs select-text">
        {isNonEmpty(value) ? value : '-'}
      </span>
    </div>
  );
};

const Register = ({ advancedRegister }: { advancedRegister: any }) => {
  const { messages } = useLocalization();
  const { formatDateWithTimeZone } = useDateTimeFormat();
  return (
    <div className="grid grid-cols-2 gap-1">
      <RegisterItem
        label={messages.shipDetailsRegisterRegisterOwnerLabel}
        value={advancedRegister?.registeredOwner}
      ></RegisterItem>
      <RegisterItem
        label={messages.shipDetailsRegisterRegisterOwnerCityLabel}
        value={advancedRegister?.registeredOwnerCity}
      ></RegisterItem>
      <RegisterItem
        label={messages.shipDetailsRegisterRegisterOwnerCountryOfDomicileLabel}
        value={advancedRegister?.registeredOwnerCountryOfDomicile}
      ></RegisterItem>
      <RegisterItem
        label={messages.shipDetailsRegisterRegisterOwnerEffectiveDateLabel}
        value={formatDateWithTimeZone(
          advancedRegister?.registeredOwnerEffectiveDate
        )}
      ></RegisterItem>
      <RegisterItem
        label={messages.shipDetailsRegisterRegisterOwnerShareLabel}
        value={advancedRegister?.registeredOwnerShare}
      ></RegisterItem>
      <RegisterItem
        label={messages.shipDetailsRegisterRegisterExpirationDateLabel}
        value={formatDateWithTimeZone(advancedRegister?.registerExpiryDate)}
      ></RegisterItem>
      <RegisterItem
        label={messages.shipDetailsRegisterRegisterPortLabel}
        value={advancedRegister?.registerPort}
      ></RegisterItem>
    </div>
  );
};

interface ShipManagerItemProps {
  label: string;
  value: string;
}

const ShipManagerItem = ({ label, value }: ShipManagerItemProps) => {
  return (
    <div className={'flex flex-col mt-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs select-text">
        {isNonEmpty(value) ? value : '-'}
      </span>
    </div>
  );
};

const ShipManager = ({ advancedRegister }: { advancedRegister: any }) => {
  const { messages } = useLocalization();
  const { formatDateWithTimeZone } = useDateTimeFormat();

  return (
    <div className="grid grid-cols-2 gap-1">
      <ShipManagerItem
        label={messages.shipDetailsRegisterShipManagerNameLabel}
        value={advancedRegister?.shipManager}
      ></ShipManagerItem>
      <RegisterItem
        label={messages.shipDetailsRegisterShipManagerCityLabel}
        value={advancedRegister?.shipManagerCity}
      ></RegisterItem>
      {/*<RegisterItem
        label={messages.shipDetailsRegisterShipManagerCountryOfControlLabel}
        value={advancedRegister.shipManagerCountryOfControl}
      ></RegisterItem> */}
      <RegisterItem
        label={messages.shipDetailsRegisterShipManagerCountryOfDomicileLabel}
        value={advancedRegister?.shipManagerCountryOfDomicile}
      ></RegisterItem>
      {/*<RegisterItem
        label={
          messages.shipDetailsRegisterShipManagerCountryOfRegistrationLabel
        }
        value={advancedRegister.shipManagerCountryOfRegistration}
      ></RegisterItem> */}
      <RegisterItem
        label={messages.shipDetailsRegisterShipManagerEffectiveDateLabel}
        value={formatDateWithTimeZone(
          advancedRegister?.shipManagerEffectiveDate
        )}
      ></RegisterItem>
    </div>
  );
};

interface ExtendedVesselRegisterInformationProps {
  extendedVesselInfo: VesselResult;
}

export const ExtendedVesselRegisterInformation = ({
  extendedVesselInfo,
}: ExtendedVesselRegisterInformationProps) => {
  const { messages } = useLocalization();

  return (
    <div className="grid gap-6">
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">
            {messages.shipDetailsRegisterRegisterTitle}
          </h3>
          <Separator className="mt-2"></Separator>
        </div>
        <Register
          advancedRegister={extendedVesselInfo?.advancedRegister}
        ></Register>
      </div>
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">
            {messages.shipDetailsRegisterISMManagerTitle}
          </h3>
          <Separator className="mt-2"></Separator>
        </div>
        <ISMManager
          advancedRegister={extendedVesselInfo?.advancedRegister}
        ></ISMManager>
      </div>

      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">
            {messages.shipDetailsRegisterShipManagerTitle}
          </h3>
          <Separator className="mt-2"></Separator>
        </div>
        <ShipManager
          advancedRegister={extendedVesselInfo?.advancedRegister}
        ></ShipManager>
      </div>
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">
            {messages.shipDetailsRegisterClassificationSocietyTitle}
          </h3>
          <Separator className="mt-2"></Separator>
        </div>
        <ClassificationSociety
          advancedRegister={extendedVesselInfo?.advancedRegister}
        ></ClassificationSociety>
      </div>
    </div>
  );
};

interface RegisterInformationProps {
  shipInfo: ShipInfo | VesselResult;
}

export const RegisterInformation = ({ shipInfo }: RegisterInformationProps) => {
  const permissions = usePermissions();

  const hasShipDbPermissions =
    permissions.mdmAdvancedVesselInfo ||
    permissions.mdmRegister ||
    permissions.mdmIntegrity;

  const {
    data: extendedVesselInfo,
    isLoading,
    isError,
  } = useGetShipByMmsi(extractMmsiFromShipInfo(shipInfo), hasShipDbPermissions);

  if (isLoading) {
    return <LoadingSpinner></LoadingSpinner>;
  }

  if (isError) {
    return <ErrorDisplay className="h-full pt-60"></ErrorDisplay>;
  }

  if (!extendedVesselInfo) {
    return <ErrorDisplay className="h-full pt-60"></ErrorDisplay>;
  }

  return (
    <ExtendedVesselRegisterInformation
      extendedVesselInfo={extendedVesselInfo}
    ></ExtendedVesselRegisterInformation>
  );
};
